/**
 * Partner Registration Page - Cadastro de PAT/Prefeituras
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { partnersAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, 
  ArrowLeft, 
  Landmark, 
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  Upload,
  CheckCircle2
} from 'lucide-react';

const ESTADOS_BRASIL = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const TIPOS_PARCEIRO = [
  { value: 'PAT', label: 'PAT - Posto de Atendimento ao Trabalhador' },
  { value: 'PREFEITURA', label: 'Prefeitura Municipal' },
  { value: 'SINE', label: 'SINE - Sistema Nacional de Emprego' },
  { value: 'OUTRO', label: 'Outra Entidade Pública' }
];

export default function PartnerRegister() {
  const navigate = useNavigate();
  const { registerPartner } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Step 1: User credentials
  const [userForm, setUserForm] = useState({
    cpf: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  // Step 2: Partner data
  const [partnerForm, setPartnerForm] = useState({
    nome: '',
    tipo: 'PAT',
    cnpj: '',
    endereco: '',
    telefone: '',
    email_contato: '',
    responsavel: '',
    municipio: '',
    estado: 'SP'
  });

  // CPF formatting
  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const isValidCPF = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
    const dv = (n) => {
      const sum = d.slice(0, n).split('').reduce((acc, c, i) => acc + Number(c) * (n + 1 - i), 0);
      return ((sum * 10) % 11) % 10;
    };
    return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
  };

  // CNPJ formatting
  const formatCNPJ = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };

  // Phone formatting
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStep1Submit = () => {
    if (!userForm.cpf || !userForm.email || !userForm.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!isValidCPF(userForm.cpf)) {
      toast.error('CPF do responsável inválido. Confira os 11 dígitos (é o CPF da pessoa, não o CNPJ da entidade).');
      return;
    }
    if (userForm.password !== userForm.confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    if (userForm.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    setStep(2);
  };

  const handleStep2Submit = async () => {
    if (!partnerForm.nome || !partnerForm.municipio || !partnerForm.estado) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      // Step 1: Create user account
      await registerPartner({
        cpf: userForm.cpf.replace(/\D/g, ''),
        email: userForm.email,
        password: userForm.password,
        ...partnerForm
      });

      if (logoFile) {
        try {
          const me = await partnersAPI.getMe();
          await partnersAPI.uploadLogo(me.data.id, logoFile);
        } catch {
          toast.warning('Cadastro criado, mas a logo não foi enviada. Envie novamente em Configurações.');
        }
      }

      toast.success('Cadastro realizado com sucesso!');
      navigate('/partner');
    } catch (error) {
      console.error('Error registering partner:', error);
      const message = error.response?.data?.detail || 'Erro ao realizar cadastro';
      toast.error(typeof message === 'string' ? message : 'Erro ao realizar cadastro');
      if (typeof message === 'string' && /CPF|Email|senha/i.test(message)) setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button 
          variant="ghost" 
          className="mb-6 text-slate-400 hover:text-white"
          onClick={() => step === 1 ? navigate('/') : setStep(1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 1 ? 'Voltar ao início' : 'Voltar'}
        </Button>

        {/* Progress */}
        <div className="flex items-center justify-center mb-8 gap-4">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-amber-400' : 'text-slate-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-amber-500 text-white' : 'bg-slate-700'}`}>
              1
            </div>
            <span className="hidden sm:inline">Credenciais</span>
          </div>
          <div className="w-12 h-1 bg-slate-700 rounded">
            <div className={`h-full rounded transition-all ${step >= 2 ? 'bg-amber-500 w-full' : 'w-0'}`} />
          </div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-amber-400' : 'text-slate-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-amber-500 text-white' : 'bg-slate-700'}`}>
              2
            </div>
            <span className="hidden sm:inline">Dados da Entidade</span>
          </div>
        </div>

        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm shadow-2xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Landmark className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              {step === 1 ? 'Cadastro de Parceiro' : 'Dados da Entidade'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {step === 1 
                ? 'Crie suas credenciais de acesso'
                : 'Informe os dados do PAT/Prefeitura'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {step === 1 ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">CPF do Responsável *</Label>
                  <Input
                    value={userForm.cpf}
                    onChange={(e) => setUserForm({...userForm, cpf: formatCPF(e.target.value)})}
                    placeholder="000.000.000-00"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    placeholder="email@entidade.gov.br"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Senha *</Label>
                    <Input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                      placeholder="••••••••"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Confirmar Senha *</Label>
                    <Input
                      type="password"
                      value={userForm.confirmPassword}
                      onChange={(e) => setUserForm({...userForm, confirmPassword: e.target.value})}
                      placeholder="••••••••"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={handleStep1Submit}
                  data-testid="partner-register-continue"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white mt-4"
                >
                  Continuar
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Logo Upload */}
                <div className="flex flex-col items-center mb-6">
                  <div 
                    className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-600 flex items-center justify-center cursor-pointer hover:border-amber-500 transition-colors overflow-hidden"
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Upload className="w-8 h-8 text-slate-500" />
                    )}
                  </div>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <p className="text-sm text-slate-400 mt-2">Logo da entidade (opcional)</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Nome da Entidade *</Label>
                  <Input
                    value={partnerForm.nome}
                    onChange={(e) => setPartnerForm({...partnerForm, nome: e.target.value})}
                    placeholder="Ex: PAT - Município de São Paulo"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Tipo *</Label>
                    <Select 
                      value={partnerForm.tipo}
                      onValueChange={(v) => setPartnerForm({...partnerForm, tipo: v})}
                    >
                      <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS_PARCEIRO.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">CNPJ</Label>
                    <Input
                      value={partnerForm.cnpj}
                      onChange={(e) => setPartnerForm({...partnerForm, cnpj: formatCNPJ(e.target.value)})}
                      placeholder="00.000.000/0000-00"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Município *</Label>
                    <Input
                      value={partnerForm.municipio}
                      onChange={(e) => setPartnerForm({...partnerForm, municipio: e.target.value})}
                      placeholder="Ex: São Paulo"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Estado *</Label>
                    <Select 
                      value={partnerForm.estado}
                      onValueChange={(v) => setPartnerForm({...partnerForm, estado: v})}
                    >
                      <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTADOS_BRASIL.map(uf => (
                          <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Endereço</Label>
                  <Input
                    value={partnerForm.endereco}
                    onChange={(e) => setPartnerForm({...partnerForm, endereco: e.target.value})}
                    placeholder="Rua, número, bairro..."
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Telefone</Label>
                    <Input
                      value={partnerForm.telefone}
                      onChange={(e) => setPartnerForm({...partnerForm, telefone: formatPhone(e.target.value)})}
                      placeholder="(00) 00000-0000"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Email de Contato</Label>
                    <Input
                      type="email"
                      value={partnerForm.email_contato}
                      onChange={(e) => setPartnerForm({...partnerForm, email_contato: e.target.value})}
                      placeholder="contato@entidade.gov.br"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Nome do Responsável</Label>
                  <Input
                    value={partnerForm.responsavel}
                    onChange={(e) => setPartnerForm({...partnerForm, responsavel: e.target.value})}
                    placeholder="Nome completo"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <Button 
                  onClick={handleStep2Submit}
                  data-testid="partner-register-submit"
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white mt-4"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cadastrando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Finalizar Cadastro
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          G2eGente - Compromisso com a empregabilidade
        </p>
      </div>
    </div>
  );
}
